import { types, getEnv, flow, SnapshotOut, Instance } from 'mobx-state-tree';
import { IAppEnv } from '../appEnvironment';
import { IGroupedApiCtx, ProgressStateModelForStore, ISBApiOperationResponse, errorIsCancel, ProgressStateModel, CouponType, BetDetailApiDto } from 'eTip--common';
import { ISportsBettingApi } from '../api/api';
import { observable, IObservableArray } from 'mobx';
import { flatten } from 'easit--utils';
import { translation } from 'easit--localization';
import { translationForTipos } from '../../../localization/localization';

export const betDetailHeaderModel = types.model({
    id: types.number,
    identifierSecure: types.string,
    stateResult: types.string,
    channelName: types.string,
    couponTypeName: types.string,
    couponPaid: types.Date,
    couponCanBeCancelled: types.boolean,
    dateCancelationLimit: types.maybeNull(types.Date),
    stake: types.number,
    rate: types.maybeNull(types.number),
    rateAdvantageFactor: types.maybeNull(types.number),
    rateAdvanced: types.maybeNull(types.number),
    prizePaidAmount: types.maybeNull(types.number),
    winPaid: types.maybeNull(types.number),
    prizePotential: types.number,
    betTypeName: types.string,
    cinchCount: types.maybeNull(types.number),
    alarmTitle: types.maybeNull(types.string),
    alarmTooltip: types.maybeNull(types.string)
});

export const bet = types.model({
    categoryName: types.string,
    resultText: types.string,
    rate: types.number,
    tipNameShort: types.string,
    eventName: types.string,
    eventDate: types.maybeNull(types.Date),
    lineNumber: types.number,
    groupName: types.maybeNull(types.string),
    resultIcon: types.maybeNull(types.string)
});

export const betTicket = types.model({
    combination: types.string,
    potentialPrize: types.number,
    rate: types.number,
    stake: types.number
});

export const betDetailStoreKey = 'ticketDetailStore';
export const betDetailStore = types
    .model(betDetailStoreKey, {
        title: types.optional(types.string, ''),
        header: types.maybeNull(betDetailHeaderModel),
        betOddsList: types.optional(types.array(bet), []),
        betTicketList: types.optional(types.array(betTicket), []),
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        publicId: types.optional(types.string, ''),
        productGUID: types.optional(types.string, ''),
        couponCanBePaidOut: types.optional(types.boolean, false),
        showCancelButton: types.optional(types.boolean, true),
        cancelState: types.maybeNull(types.number)
    })
    .views(() => ({}))
    .actions(self => {
        const env = getEnv<IAppEnv>(self);
        let synchroCtx: IGroupedApiCtx<ISportsBettingApi> | null = null;

        const loadAsync: (couponId: number) => Promise<void> = flow(function* (couponId: number) {
            let ctx: IGroupedApiCtx<ISportsBettingApi> | null = null;
            try {
                if (synchroCtx && synchroCtx.isOpen()) {
                    synchroCtx.cancel();
                    synchroCtx = null;
                }
                ctx = env.api.open();
                synchroCtx = ctx;
                self.state = ProgressStateModel.Loading;
                self.title = translation().ticket_detail;

                const r: ISBApiOperationResponse<BetDetailApiDto> = yield ctx.api.getBetDetailAsync(couponId, env.settings.legacySBUrl);
                setBetDetail(r.ReturnValue, false, ctx.api.toDate);
                self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                if (synchroCtx === ctx) {
                    self.state = ProgressStateModel.Failed;
                }
            } finally {
                if (synchroCtx === ctx) {
                    synchroCtx = null;
                }
            }
        });

        const loadPublicAsync: (publicId: string) => Promise<void> = flow(function* (publicId: string) {
            let ctx: IGroupedApiCtx<ISportsBettingApi> | null = null;
            try {
                if (synchroCtx && synchroCtx.isOpen()) {
                    synchroCtx.cancel();
                    synchroCtx = null;
                }
                ctx = env.api.open();
                synchroCtx = ctx;
                self.state = ProgressStateModel.Loading;
                self.title = translation().ticket_detail;
                const r: ISBApiOperationResponse<BetDetailApiDto> = yield ctx.api.getBetDetailPublicAsync(publicId, env.settings.legacySBUrl);
                setBetDetail(r.ReturnValue, true, ctx.api.toDate);
                self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                if (synchroCtx === ctx) {
                    self.state = ProgressStateModel.Failed;
                }
            } finally {
                if (synchroCtx === ctx) {
                    synchroCtx = null;
                }
            }
        });

        const loadWeekHitAsync: (type: number) => Promise<void> = flow(function* (type: number) {
            let ctx: IGroupedApiCtx<ISportsBettingApi> | null = null;
            try {
                if (synchroCtx && synchroCtx.isOpen()) {
                    synchroCtx.cancel();
                    synchroCtx = null;
                }
                ctx = env.api.open();
                synchroCtx = ctx;
                self.state = ProgressStateModel.Loading;
                const r: ISBApiOperationResponse<BetDetailApiDto> = yield ctx.api.getWeekHitAsync(type);
                setBetDetail(r.ReturnValue, true, ctx.api.toDate);
                self.title = type ? translationForTipos().loser_of_the_week : translationForTipos().hit_of_the_week;
                self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                if (synchroCtx === ctx) {
                    self.state = ProgressStateModel.Failed;
                }
            } finally {
                if (synchroCtx === ctx) {
                    synchroCtx = null;
                }
            }
        });

        const createBetOddsList = (r: BetDetailApiDto, toDateConverter: (val: string | null) => Date): IObservableArray<Instance<typeof bet>> => {
            switch (r.CouponType) {
                case CouponType.System:
                    return observable(flatten(
                        r.BetGroupList.map(bg => {
                            return bg.BetOddsList.map(b =>
                                bet.create(<SnapshotOut<typeof bet>>{
                                    categoryName: b.CategoryName,
                                    resultText: b.ResultText,
                                    rate: b.Rate,
                                    tipNameShort: b.TipNameShort,
                                    eventName: b.EventName,
                                    eventDate: toDateConverter(b.EventDate)?.getTime(),
                                    lineNumber: b.LineNumber,
                                    groupName: bg.GroupName,
                                    resultIcon: b.ResultIcon
                                })
                            );
                        })
                    ));
                default:
                    return observable(
                        r.BetOddsList.map(b => {
                            try {
                                return bet.create(<SnapshotOut<typeof bet>>{
                                    categoryName: b.CategoryName,
                                    resultText: b.ResultText,
                                    rate: b.Rate,
                                    tipNameShort: b.TipNameShort,
                                    eventName: b.EventName,
                                    eventDate: toDateConverter(b.EventDate)?.getTime(),
                                    lineNumber: b.LineNumber,
                                    resultIcon: b.ResultIcon
                                });
                            } catch (error) {
                                env.loggingService.error(error);
                            }
                            return undefined;
                        })
                    );
            }
        };

        const createHeaderData = (r: BetDetailApiDto, toDateConverter: (value: string) => Date): Instance<typeof betDetailHeaderModel> => {
            let header: Instance<typeof betDetailHeaderModel> = {
                id: r.ID,
                identifierSecure: r.IdentifierSecure,
                stateResult: r.StateResult,
                channelName: r.ChannelName,
                couponTypeName: r.CouponTypeName,
                couponPaid: toDateConverter(r.CouponPaid),
                couponCanBeCancelled: r.CouponCanBeCancelled,
                dateCancelationLimit: r.DateCancelationLimit ? toDateConverter(r.DateCancelationLimit) : null,
                stake: r.Stake,
                rate: null,
                rateAdvanced: null,
                rateAdvantageFactor: null,
                prizePaidAmount: r.PrizePaidAmount,
                winPaid: r.WinPay,
                prizePotential: r.PrizePotential,
                betTypeName: r.BetTypeName,
                cinchCount: r.SystemsCinchCount,
                alarmTitle: r.CouponAlarmTitle,
                alarmTooltip: r.CouponAlarmToolTip
            };

            if (!!r.RateOdds && r.CouponType != 3) {
                header.rate = r.RateOdds;
                header.rateAdvanced = r.Rate;
                header.rateAdvantageFactor = r.RateAdvantage;
            } else {
                header.rate = r.Rate;
            }

            return betDetailHeaderModel.create(header);
        };

        const setBetDetail = (r: BetDetailApiDto, isFromPublicId: boolean, toDateConverter: (value: string | null) => Date) => {
            self.header = createHeaderData(r, toDateConverter);

            self.betTicketList = observable(
                r.BetTicketsList.map(bt =>
                    betTicket.create(<SnapshotOut<typeof betTicket>>{
                        combination: bt.Combination,
                        potentialPrize: bt.PrizePotential,
                        rate: bt.Rate,
                        stake: bt.Stake
                    })
                )
            );

            self.betOddsList = createBetOddsList(r, toDateConverter);
            self.publicId = r.PblId;
            self.productGUID = r.ProductGUID;
            self.couponCanBePaidOut = !isFromPublicId && r.CouponCanBePaidOut;
            self.showCancelButton = !isFromPublicId && r.CouponCanBeCancelled;
        };

        return {
            loadAsync,
            loadPublicAsync,
            loadWeekHitAsync
        };
    });
